import { useCallback, useContext } from "react";
import axios from "axios";
import FileDownload from "js-file-download";
import dayjs from "dayjs";
import streamSaver from "streamsaver";
import { WritableStream } from "web-streams-polyfill/ponyfill";

import firebase from "../../../firebase";
import { UserContext } from "../../../context";
import { useTransfers } from "../../../transferContext";

streamSaver.mitm = "/mitm.html";

const isSafari = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        userAgent.includes("safari") &&
        !userAgent.includes("chrome") &&
        !userAgent.includes("chromium")
    );
};

function readWithTimeout(reader, timeoutMs) {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error("Download stalled"));
        }, timeoutMs);

        reader.read().then(
            (result) => {
                clearTimeout(timeoutId);
                resolve(result);
            },
            (error) => {
                clearTimeout(timeoutId);
                reject(error);
            },
        );
    });
}

async function recordDownloadUsage(file, currentUserData) {
    if (!file || !currentUserData) {
        return;
    }

    try {
        await firebase.database().ref("fileusage").push({
            file: file.key,
            fileName: file.displayName,
            fileType: file.type,
            user: currentUserData.key,
            action: "Download",
            date: dayjs().toString(),
        });
    } catch (error) {
        console.error("Unable to record file download usage", error);
    }
}

function triggerBrowserDownload(blob, fileName) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
}

export function useBlobFileDownload() {
    const { currentUserData } = useContext(UserContext);
    const {
        startTransfer,
        updateTransfer,
        completeTransfer,
        failTransfer,
        cancelTransfer,
        isTransferActive,
        requestPanelOpen,
    } = useTransfers();

    const downloadFile = useCallback(
        async (file) => {
            if (!file) {
                return;
            }

            if (isTransferActive("download", file.key)) {
                requestPanelOpen();
                return;
            }

            const cancelSource = axios.CancelToken.source();
            const transferId = startTransfer({
                type: "download",
                fileKey: file.key,
                name: file.filename || file.displayName,
                fileType: file.type,
                status: "active",
                message: "Downloading",
                cancel: () => cancelSource.cancel("Download cancelled"),
            });

            try {
                const response = await axios({
                    url: file.url,
                    method: "GET",
                    responseType: "blob",
                    cancelToken: cancelSource.token,
                    onDownloadProgress: (progressEvent) => {
                        updateTransfer(transferId, {
                            status: "active",
                            loaded: progressEvent.loaded || 0,
                            total: progressEvent.total || 0,
                            message: "Downloading",
                        });
                    },
                });

                FileDownload(
                    response.data,
                    file.filename || file.displayName,
                );
                await recordDownloadUsage(file, currentUserData);
                completeTransfer(transferId, {
                    loaded: response.data.size || 0,
                    total: response.data.size || 0,
                    message: "Download complete",
                });
            } catch (error) {
                if (axios.isCancel(error)) {
                    cancelTransfer(transferId);
                    return;
                }

                console.error("Download error:", error);
                const message = navigator.onLine
                    ? "Download failed"
                    : "Download failed — you appear to be offline";

                failTransfer(transferId, message, () =>
                    downloadFile(file),
                );
            }
        },
        [
            currentUserData,
            startTransfer,
            updateTransfer,
            completeTransfer,
            failTransfer,
            cancelTransfer,
            isTransferActive,
            requestPanelOpen,
        ],
    );

    return downloadFile;
}

export function useVideoFileDownload() {
    const { currentUserData } = useContext(UserContext);
    const {
        startTransfer,
        updateTransfer,
        completeTransfer,
        failTransfer,
        warnTransfer,
        cancelTransfer,
        isTransferActive,
        requestPanelOpen,
    } = useTransfers();

    const downloadVideo = useCallback(
        async (file) => {
            if (!file) {
                return;
            }

            if (isTransferActive("download", file.key)) {
                requestPanelOpen();
                return;
            }

            const downloadFileKey = file.original
                ? file.original
                : file.key;
            const url =
                "https://vz-db0e7359-d70.b-cdn.net/" +
                downloadFileKey +
                "/original";
            const abortController = new AbortController();
            const transferId = startTransfer({
                type: "download",
                fileKey: file.key,
                name: file.filename || file.displayName,
                fileType: file.type,
                status: "preparing",
                message: "Preparing download",
                cancel: () => abortController.abort(),
            });

            let writer;
            let loaded = 0;
            let total = 0;
            let lastProgressUpdate = 0;

            const reportProgress = (force = false) => {
                const now = Date.now();

                if (!force && now - lastProgressUpdate < 200) {
                    return;
                }

                lastProgressUpdate = now;
                updateTransfer(transferId, {
                    status: "active",
                    loaded,
                    total,
                    message: "Downloading",
                });
            };

            try {
                if (isSafari()) {
                    try {
                        const headResponse = await fetch(url, {
                            method: "HEAD",
                            signal: abortController.signal,
                        });
                        const contentLength =
                            headResponse.headers.get("content-length");
                        const fileSize = contentLength
                            ? parseInt(contentLength, 10)
                            : 0;
                        const safariBlobLimit = 500 * 1024 * 1024;

                        if (fileSize > 0 && fileSize < safariBlobLimit) {
                            total = fileSize;
                            reportProgress(true);

                            const response = await fetch(url, {
                                signal: abortController.signal,
                            });

                            if (!response.ok) {
                                throw new Error(
                                    `Server error: ${response.status}`,
                                );
                            }

                            const reader = response.body.getReader();
                            const chunks = [];

                            while (true) {
                                const { done, value } =
                                    await readWithTimeout(reader, 30000);

                                if (done) {
                                    break;
                                }

                                chunks.push(value);
                                loaded += value.length;
                                reportProgress();
                            }

                            const blob = new Blob(chunks, {
                                type: file.type,
                            });
                            triggerBrowserDownload(
                                blob,
                                file.filename || file.displayName,
                            );
                            reportProgress(true);
                            await recordDownloadUsage(
                                file,
                                currentUserData,
                            );
                            completeTransfer(transferId, {
                                loaded,
                                total: total || loaded,
                                message: "Download complete",
                            });
                            return;
                        }

                        warnTransfer(
                            transferId,
                            "Large downloads can be unreliable in Safari. You can close this download menu and continue using Nodiant, but keep this browser tab open. For best results, use Chrome or Firefox.",
                        );
                    } catch (error) {
                        if (abortController.signal.aborted) {
                            cancelTransfer(transferId);
                            return;
                        }

                        console.error(
                            "Safari file size check error:",
                            error,
                        );
                    }
                }

                if (!window.WritableStream) {
                    streamSaver.WritableStream = WritableStream;
                    window.WritableStream = WritableStream;
                }

                const fileStream = streamSaver.createWriteStream(
                    file.filename || file.displayName,
                    {},
                );
                writer = fileStream.getWriter();

                const response = await fetch(url, {
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`Server error: ${response.status}`);
                }

                const contentLength =
                    response.headers.get("content-length");
                total = contentLength
                    ? parseInt(contentLength, 10)
                    : 0;
                reportProgress(true);

                const largeFileThreshold = 100 * 1024 * 1024;
                if (total > largeFileThreshold && !isSafari()) {
                    warnTransfer(
                        transferId,
                        "This is a large download. You can close this download menu and continue using Nodiant, but keep this browser tab open until the download finishes.",
                    );
                }

                const reader = response.body.getReader();

                while (true) {
                    const { done, value } = await readWithTimeout(
                        reader,
                        30000,
                    );

                    if (done) {
                        break;
                    }

                    loaded += value.length;
                    reportProgress();
                    await writer.write(value);
                }

                await writer.close();
                reportProgress(true);
                await recordDownloadUsage(file, currentUserData);
                completeTransfer(transferId, {
                    loaded,
                    total: total || loaded,
                    message: "Download complete",
                });
            } catch (error) {
                try {
                    abortController.abort();
                } catch (abortError) {}

                if (writer) {
                    try {
                        await writer.abort();
                    } catch (writerError) {}
                }

                if (
                    abortController.signal.aborted &&
                    (error.name === "AbortError" ||
                        error.message === "Download cancelled")
                ) {
                    cancelTransfer(transferId);
                    return;
                }

                console.error("Download error:", error);

                let errorMessage = "Download failed";

                if (error.message?.includes("stalled")) {
                    errorMessage =
                        "Download stalled — please check your connection";
                } else if (!navigator.onLine) {
                    errorMessage =
                        "Download failed — you appear to be offline";
                }

                if (isSafari()) {
                    errorMessage +=
                        ". Safari can have problems with large downloads; try Chrome or Firefox.";
                }

                failTransfer(transferId, errorMessage, () =>
                    downloadVideo(file),
                );
            }
        },
        [
            currentUserData,
            startTransfer,
            updateTransfer,
            completeTransfer,
            failTransfer,
            warnTransfer,
            cancelTransfer,
            isTransferActive,
            requestPanelOpen,
        ],
    );

    return downloadVideo;
}
